/**
 * SegmentedRow — a small closed set of choices, all visible at once
 * ("Middle / Lower", "Let us decide / 1 / 2 / 3").
 *
 * No preview step: a segment is a discrete choice, so the click IS the
 * commit — one journal entry, undoable, labelled with the segment's own
 * words ("Caption position → Lower"), not with the field name behind it.
 *
 * Re-picking the current value is a no-op: an "edit" that changes nothing
 * must never earn a journal entry (it would materialise a field into a
 * later channel promotion for free).
 */
import type { EditPatch } from "../../../../../src/lib/clipEditState.ts";
import { fieldLabelStyle } from "../../styles.ts";
import type { EssentialRowProps } from "./types.ts";

export function SegmentedRow<T extends string>(
  p: {
    label: string;
    value: T;
    options: { id: T; label: string }[];
    toPatch: (v: T) => EditPatch;
    commitLabel: string;
  } & EssentialRowProps,
): React.JSX.Element {
  const { label, value, options, toPatch, commitLabel, onCommit } = p;
  return (
    <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
      <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>{label}</span>
      <div style={{ display: "flex", gap: 5, minWidth: 0 }}>
        {options.map((o) => {
          const active = o.id === value;
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={active}
              onClick={() => {
                if (!active) onCommit(toPatch(o.id), `${commitLabel} → ${o.label}`);
              }}
              style={{
                flex: 1,
                minWidth: 0,
                padding: "7px 6px",
                borderRadius: 8,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 11.5,
                fontWeight: 700,
                lineHeight: 1.2,
                border: "1px solid " + (active ? "var(--ql-yellow)" : "var(--ql-border)"),
                background: active ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.03)",
                color: active ? "var(--ql-fg)" : "var(--ql-fg-muted)",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
